<template>
  <div class="holiday-remote">
    <v-btn
      v-if="showButton"
      color="primary"
      variant="elevated"
      @click="openDialog"
      prepend-icon="mdi-calendar"
    >
      공휴일 관리
    </v-btn>

    <v-dialog v-model="dialog" max-width="800px" >
      <v-card>
        <v-card-title class="dialog-title">
          공휴일 관리
          <v-spacer></v-spacer>
          <v-btn icon variant="text" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <div class="year-selector">
            <v-select
              v-model="selectedYear"
              :items="yearOptions"
              label="연도 선택"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="loadHolidays"
              class="year-select"
            />
          </div>

          <div class="holidays-grid">
            <div
              v-for="month in 12"
              :key="month"
              class="month-card"
            >
              <div class="month-header">
                <span class="month-title">{{ month }}월</span>
                <v-btn
                  color="primary"
                  variant="outlined"
                  @click="addHoliday(month)"
                  prepend-icon="mdi-plus"
                >
                  추가
                </v-btn>
              </div>

              <div class="holidays-list">
                <div
                  v-for="holiday in getHolidaysByMonth(month)"
                  :key="holiday.hh_idx || holiday.tempId"
                  :data-holiday-id="holiday.tempId"
                  class="holiday-item"
                >
                  <div class="holiday-inputs">
                    <v-text-field
                      v-model="holiday.day"
                      type="number"
                      min="1"
                      :max="getDaysInMonth(month)"
                      variant="outlined"
                      density="compact"
                      hide-details
                      class="day-input"
                      @update:model-value="updateHolidayDate(holiday, month)"
                      @blur="handleDayBlur(holiday, month)"
                    />
                    <span class="day-label">일</span>
                    <v-text-field
                      v-model="holiday.hh_holiday_name"
                      variant="outlined"
                      density="compact"
                      hide-details
                      placeholder="공휴일명"
                      class="name-input"
                      @input="validateHolidayName(holiday)"
                    />
                  </div>
                  <v-btn
                    color="error"
                    variant="outlined"
                    @click="removeHoliday(holiday)"
                    prepend-icon="mdi-delete"
                  >
                    삭제
                  </v-btn>
                </div>

                <div v-if="getHolidaysByMonth(month).length === 0" class="no-holidays">
                  <v-icon color="grey">mdi-calendar-blank</v-icon>
                  <span>공휴일 없음</span>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="dialog = false">취소</v-btn>
          <v-btn color="primary" variant="elevated" @click="saveHolidays" :loading="saving">
            <v-icon class="mr-1">mdi-content-save</v-icon>
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">닫기</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import axios from 'axios';

// Props
interface Props {
  showButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showButton: false
});

interface Holiday {
  hh_idx?: number;
  hh_date: string;
  hh_holiday_name: string;
  day?: number;
  isNew?: boolean;
  isDeleted?: boolean;
  tempId?: string;
  isValid?: boolean;
}

const dialog = ref(false);
const selectedYear = ref(new Date().getFullYear());
const holidays = ref<Holiday[]>([]);
const saving = ref(false);
const snackbar = ref({ 
  show: false, 
  message: '', 
  color: 'success',
  icon: 'mdi-check-circle'
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
});

// 날짜를 YYYY-MM-DD 형식으로 변환하는 함수
const formatDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString) return '';
  
  // 이미 YYYY-MM-DD 형식인 경우 그대로 반환
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  
  // ISO 8601 형식 또는 다른 날짜 형식인 경우 Date 객체로 변환
  const date = new Date(dateString);
  
  // 유효하지 않은 날짜인 경우 빈 문자열 반환
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // YYYY-MM-DD 형식으로 변환
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const loadHolidays = async () => {
  try {
    const response = await axios.get(`/api/calc/holidays/${selectedYear.value}`);
    if (response.data.success) {
      holidays.value = response.data.data.map((h: Holiday) => ({
        ...h,
        day: new Date(h.hh_date).getDate(),
        isNew: false,
        isDeleted: false,
        isValid: true
      }));
    }
  } catch (error) {
    console.error('공휴일 조회 실패:', error);
    showSnackbar('공휴일 조회에 실패했습니다', 'error', 'mdi-alert-circle');
  }
};

const getHolidaysByMonth = (month: number) => {
  return holidays.value.filter(h => {
    if (h.isDeleted) return false;
    const date = new Date(h.hh_date);
    return date.getMonth() + 1 === month && date.getFullYear() === selectedYear.value;
  });
};

const getDaysInMonth = (month: number) => {
  return new Date(selectedYear.value, month, 0).getDate();
};

const addHoliday = (month: number) => {
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const newHoliday: Holiday = {
    hh_date: `${selectedYear.value}-${String(month).padStart(2, '0')}-01`,
    hh_holiday_name: '',
    day: 1,
    isNew: true,
    isDeleted: false,
    tempId,
    isValid: false
  };
  
  holidays.value.push(newHoliday);
  
  // 새로 추가된 항목에 포커스
  nextTick(() => {
    const nameInput = document.querySelector(`[data-holiday-id="${tempId}"] .name-input input`) as HTMLInputElement;
    if (nameInput) {
      nameInput.focus();
    }
  });
  
  showSnackbar(`${month}월에 공휴일을 추가했습니다`, 'info', 'mdi-plus-circle');
};

const updateHolidayDate = (holiday: Holiday, month: number) => {
  // day가 빈 값이거나 null/undefined인 경우 처리하지 않음
  if (holiday.day === null || holiday.day === undefined || holiday.day === '') {
    // 백스페이스로 지운 경우 이전 값으로 복원하지 않음 (사용자가 입력 중일 수 있음)
    return;
  }
  
  const day = Number(holiday.day);
  const maxDay = getDaysInMonth(month);
  
  // 유효하지 않은 숫자인 경우 처리하지 않음
  if (isNaN(day) || day < 1) {
    return;
  }
  
  // 일자 유효성 검사
  if (day > maxDay) {
    holiday.day = maxDay;
    showSnackbar(`${month}월은 ${maxDay}일까지만 있습니다`, 'warning', 'mdi-alert');
  } else {
    holiday.day = day;
  }
  
  // 유효한 day 값이 있을 때만 날짜 업데이트
  if (holiday.day && holiday.day >= 1 && holiday.day <= maxDay) {
    holiday.hh_date = `${selectedYear.value}-${String(month).padStart(2, '0')}-${String(holiday.day).padStart(2, '0')}`;
  }
  
  validateHolidayName(holiday);
};

// 포커스를 잃었을 때 빈 값이면 기본값으로 복원
const handleDayBlur = (holiday: Holiday, month: number) => {
  if (holiday.day === null || holiday.day === undefined || holiday.day === '') {
    // 이전에 유효한 날짜가 있었으면 그것으로 복원, 없으면 1로 설정
    if (holiday.hh_date) {
      const date = new Date(holiday.hh_date);
      holiday.day = date.getDate();
    } else {
      holiday.day = 1;
    }
    updateHolidayDate(holiday, month);
  }
};

const validateHolidayName = (holiday: Holiday) => {
  const name = holiday.hh_holiday_name?.trim();
  holiday.isValid = Boolean(name && name.length > 0);
  
  if (name && name.length > 50) {
    holiday.hh_holiday_name = name.substring(0, 50);
    showSnackbar('공휴일명은 50자 이내로 입력해주세요', 'warning', 'mdi-alert');
  }
};

const removeHoliday = (holiday: Holiday) => {
  if (holiday.isNew) {
    const index = holidays.value.indexOf(holiday);
    holidays.value.splice(index, 1);
    showSnackbar('공휴일을 삭제했습니다', 'info', 'mdi-delete');
  } else {
    holiday.isDeleted = true;
    showSnackbar('공휴일을 삭제 목록에 추가했습니다', 'warning', 'mdi-delete-outline');
  }
};

const saveHolidays = async () => {
  // 유효성 검사
  const invalidHolidays = holidays.value.filter(h => 
    !h.isDeleted && (!h.hh_holiday_name?.trim() || !h.day)
  );
  
  if (invalidHolidays.length > 0) {
    showSnackbar('공휴일명과 일자를 모두 입력해주세요', 'error', 'mdi-alert-circle');
    return;
  }

  saving.value = true;
  try {
    let count = 0;
    const errors: string[] = [];

    // 삭제
    const deletes = holidays.value.filter(h => h.isDeleted && h.hh_idx);
    for (const h of deletes) {
      try {
        await axios.delete(`/api/calc/holidays/${h.hh_idx}`);
        count++;
      } catch (error) {
        errors.push(`삭제 실패: ${h.hh_holiday_name}`);
      }
    }

    // 추가
    const inserts = holidays.value.filter(h => h.isNew && !h.isDeleted && h.hh_holiday_name.trim());
    for (const h of inserts) {
      try {
        const formattedDate = formatDateToYYYYMMDD(h.hh_date);
        if (!formattedDate) {
          errors.push(`날짜 형식 오류: ${h.hh_holiday_name}`);
          continue;
        }
        await axios.post('/api/calc/holidays', {
          hh_date: formattedDate,
          hh_holiday_name: h.hh_holiday_name
        });
        count++;
      } catch (error) {
        errors.push(`추가 실패: ${h.hh_holiday_name}`);
      }
    }

    // 수정
    const updates = holidays.value.filter(h => !h.isNew && !h.isDeleted && h.hh_idx);
    for (const h of updates) {
      try {
        const formattedDate = formatDateToYYYYMMDD(h.hh_date);
        if (!formattedDate) {
          errors.push(`날짜 형식 오류: ${h.hh_holiday_name}`);
          continue;
        }
        await axios.put(`/api/calc/holidays/${h.hh_idx}`, {
          hh_date: formattedDate,
          hh_holiday_name: h.hh_holiday_name
        });
        count++;
      } catch (error) {
        errors.push(`수정 실패: ${h.hh_holiday_name}`);
      }
    }

    if (errors.length > 0) {
      showSnackbar(`일부 저장 실패: ${errors.length}건`, 'warning', 'mdi-alert');
    } else {
      showSnackbar(`저장 완료! (${count}건 처리)`, 'success', 'mdi-check-circle');
    }
    
    await loadHolidays();
  } catch (error) {
    console.error('저장 실패:', error);
    showSnackbar('저장 중 오류가 발생했습니다', 'error', 'mdi-alert-circle');
  } finally {
    saving.value = false;
  }
};

const showSnackbar = (message: string, color: string = 'success', icon: string = 'mdi-check-circle') => {
  snackbar.value = { show: true, message, color, icon };
};

// 다이얼로그 열릴 때 데이터 로드
const openDialog = () => {
  dialog.value = true;
  loadHolidays();
};

// 외부에서 접근 가능하도록 노출
defineExpose({
  openDialog
});
</script>

<style lang="scss" scoped>
@import './HolidayRemote.scss';
</style>

